/* global React, Appear, Counter, useInView, CasePlaceholder, CaseMetric, OrbAle, CaseBreadcrumb, FooterCaseCondensed, LangToggle */
const { useState, useEffect, useRef } = React;

// ─── ALE · SaleADS — los cuatro estados del personaje agéntico ─────────────
// tag/role/body/alt como { es, en } para internacionalización fiel.
const ALE_STATES = [
  {
    tag: { es: "Sólido", en: "Solid" },
    role: { es: "en espera", en: "idle" },
    body: { es: "Presente, disponible. No interrumpe — espera.", en: "Present, available. Doesn't interrupt — waits." },
    img: "assets/cases/ALE/estado-solido.webp",
    fallback: "assets/cases/ALE/estado-solido.png",
    alt: {
      es: "Esfera glassy color violeta-azul con dos ojos verticales en cápsula blanca centrados — superficie lisa y reflejos definidos, transmite quietud y presencia.",
      en: "Glassy violet-blue sphere with two vertical eyes in a white capsule, centered — smooth surface and crisp reflections, conveying stillness and presence.",
    },
  },
  {
    tag: { es: "Líquido", en: "Liquid" },
    role: { es: "procesando", en: "processing" },
    body: { es: "Pensando, en movimiento. La señal de que algo ocurre.", en: "Thinking, in motion. The signal that something's happening." },
    img: "assets/cases/ALE/estado-liquido.webp",
    fallback: "assets/cases/ALE/estado-liquido.png",
    alt: {
      es: "Esfera con superficie líquida turbulenta en violeta, turquesa y rosa, partículas brillantes dispersas y los mismos ojos verticales — sensación de movimiento interno y proceso activo.",
      en: "Sphere with a turbulent liquid surface in violet, teal and pink, scattered bright particles and the same vertical eyes — sense of internal motion and active processing.",
    },
  },
  {
    tag: { es: "Gaseoso", en: "Gaseous" },
    role: { es: "insight", en: "insight" },
    body: { es: "Celebración, momento clave. Cuando hay algo que mostrar.", en: "Celebration, key moment. When there's something to show." },
    img: "assets/cases/ALE/estado-gaseoso.webp",
    fallback: "assets/cases/ALE/estado-gaseoso.png",
    alt: {
      es: "Esfera disuelta en partículas de neón turquesa y magenta que se expanden hacia afuera, ojos en el centro flotando — momento de explosión expresiva, insight.",
      en: "Sphere dissolved into neon teal and magenta particles expanding outward, eyes floating in the center — moment of expressive burst, insight.",
    },
  },
  {
    tag: { es: "Offline", en: "Offline" },
    role: { es: "inactivo", en: "inactive" },
    body: { es: "Fuera de escena, sin ruido.", en: "Out of scene, no noise." },
    img: "assets/cases/ALE/estado-offline.webp",
    fallback: "assets/cases/ALE/estado-offline.png",
    alt: {
      es: "Esfera oscura mate con apenas un leve gradiente y los ojos en blanco translúcido tenue — ELIA en estado dormido, fuera de escena.",
      en: "Dark matte sphere with a faint gradient and the eyes in soft translucent white — ELIA asleep, out of scene.",
    },
  },
];

// Helper local para campos { es, en }.
function aleL(v, lang) {
  if (v && typeof v === "object" && (v.es || v.en)) return v[lang] || v.es || v.en;
  return v;
}

const ALE_TOOLKIT = ["Claude", "Grok", "Lovable", "Figma (make & Jam)", "Pencil", "Mintlify", "Discord"];

// ─── Reviews del equipo cliente — cargos sin nombre de empresa por NDA ───
const ALE_REVIEWS = [
  {
    name: "Cristian Muñoz",
    role: { es: "Director of Innovation", en: "Director of Innovation" },
    text: {
      es: "Gracias por tu dedicación y esfuerzo. Me gusta mucho que desde el inicio hayas impactado desde tu perspectiva en todo.",
      en: "Thank you for your dedication and effort. I really like that from the start you've had an impact, from your own perspective, on everything.",
    },
  },
  {
    name: "Jamilton Alonso Quintero Osorio",
    role: { es: "Founder & CTO", en: "Founder & CTO" },
    text: {
      es: "La velocidad con la que entregaste tanta calidad me impresionó. Aportaste mucho valor y fuiste un gran impulso para que el equipo sacara algo muy importante en tiempo récord.",
      en: "The speed at which you delivered so much quality impressed me. You brought a lot of value and you were a major push for the team to ship something really important in record time.",
    },
  },
];

// ─── ALE · datos que informaron el rediseño ───────────────────────────────
const ALE_DATOS_BASE = "assets/cases/ALE";
const ALE_DATOS = [
  {
    src: `${ALE_DATOS_BASE}/datos-1.webp`,
    fallback: `${ALE_DATOS_BASE}/datos-1.png`,
    kicker: { es: "Dónde se rompía el flujo", en: "Where the flow was breaking" },
    body: {
      es: "Crucé el volumen de conversaciones con la tasa de fricción por clúster. Uno de los clústeres críticos concentraba casi el 60% de fricción. Los datos me marcaron la ruta exacta: ahí era donde necesitábamos la intervención proactiva de ELIA. Ya no podía quedarse mirando.",
      en: "I crossed conversation volume against friction rate per cluster. One critical cluster concentrated almost 60% of the friction. The data drew the exact route: that's where we needed ELIA's proactive intervention. She couldn't just watch anymore.",
    },
    alt: {
      es: "Captura de pantalla de un dashboard analítico con gráfico de barras y dispersión cruzando volumen de conversaciones contra tasa de fricción por clúster temático.",
      en: "Screenshot of an analytics dashboard with a bar and scatter chart crossing conversation volume against friction rate per topic cluster.",
    },
  },
  {
    src: `${ALE_DATOS_BASE}/datos-2.webp`,
    fallback: `${ALE_DATOS_BASE}/datos-2.png`,
    kicker: { es: "La carga cognitiva", en: "The cognitive load" },
    body: {
      es: "Medir la cantidad de palabras por clúster me dejó ver qué tan pesada era la charla. Si ELIA iba a pasar de asistente pasivo a agente proactivo, tenía que dejar de tirar muros de texto. Definimos nuevos límites para que guíe, no para que abrume.",
      en: "Measuring word count per cluster let me see how heavy the conversation got. If ELIA was going from passive assistant to proactive agent, she had to stop dropping walls of text. We set new limits — guide, don't overwhelm.",
    },
    alt: {
      es: "Captura de pantalla de un gráfico de violines comparando la distribución de cantidad de palabras por mensaje en cada clúster de conversación.",
      en: "Screenshot of a violin chart comparing the distribution of word count per message across each conversation cluster.",
    },
  },
  {
    src: `${ALE_DATOS_BASE}/datos-3.webp`,
    fallback: `${ALE_DATOS_BASE}/datos-3.png`,
    kicker: { es: "El costo de la frustración", en: "The cost of frustration" },
    body: {
      es: "Comparé el largo de las interacciones en casos de éxito versus casos con fricción. La evidencia es simple: cuando el usuario choca, habla más y se desgasta. ELIA tenía que entrar en escena para cortar esa fricción antes de que el usuario abandonara el flujo.",
      en: "I compared interaction length in successful cases vs. friction cases. The evidence is simple: when a user hits a wall, they talk more and burn out faster. ELIA had to step in to cut that friction before the user abandoned the flow.",
    },
    alt: {
      es: "Captura de pantalla de un gráfico comparando el largo promedio de las respuestas del usuario en conversaciones exitosas frente a conversaciones con fricción.",
      en: "Screenshot of a chart comparing average user-reply length in successful conversations vs. high-friction conversations.",
    },
  },
  {
    src: `${ALE_DATOS_BASE}/datos-4.webp`,
    fallback: `${ALE_DATOS_BASE}/datos-4.png`,
    kicker: { es: "El mapa del enojo", en: "The map of anger" },
    body: {
      es: "Crucé el análisis de sentimiento con los clústeres temáticos. Ver exactamente dónde se concentraba la negatividad fue la base para escribir el manual de ELIA: cómo maneja los errores, cómo interrumpe y cómo reacciona ante la frustración del usuario.",
      en: "I crossed sentiment analysis with the topic clusters. Seeing exactly where the negativity concentrated was the foundation for writing ELIA's manual: how she handles errors, when she interrupts, how she reacts to user frustration.",
    },
    alt: {
      es: "Captura de pantalla de un análisis NLP con score de sentimiento cruzado contra clústeres temáticos, destacando zonas de carga negativa.",
      en: "Screenshot of an NLP analysis with sentiment score crossed against topic clusters, highlighting zones of negative load.",
    },
  },
  {
    src: `${ALE_DATOS_BASE}/datos-5.webp`,
    fallback: `${ALE_DATOS_BASE}/datos-5.png`,
    kicker: { es: "El puente entre agentes", en: "The bridge between agents" },
    body: {
      es: "La visión era tener múltiples agentes de IA trabajando en paralelo. Este mapa de calor me permitió diseñar el enrutamiento: cómo hace ELIA para pasarle la tarea al agente correcto en el momento correcto, sin que el usuario sienta el salto de sistema.",
      en: "The vision was to have multiple AI agents working in parallel. This heatmap let me design the routing: how ELIA hands off the right task to the right agent at the right moment, without the user feeling the system jump.",
    },
    alt: {
      es: "Captura de pantalla de un mapa de calor que cruza clústeres temáticos contra sub-agentes de IA, mostrando intensidad de derivación entre ambos.",
      en: "Screenshot of a heatmap crossing topic clusters against AI sub-agents, showing handoff intensity between them.",
    },
  },
];

// ─── Carrusel reutilizable para muestras de trabajo ───────────────────────
function CaseCarousel({ slides, maxHeight = 600, ariaLabel, lang = "es" }) {
  const t = (es, en) => (lang === "en" ? en : es);
  const resolvedAriaLabel = ariaLabel || t("Galería", "Gallery");
  const [i, setI] = useState(0);
  const rootRef = useRef(null);
  const total = slides.length;
  const wrap = (n) => ((n % total) + total) % total;
  const go = (n) => setI(wrap(n));
  const prev = () => setI((curr) => wrap(curr - 1));
  const next = () => setI((curr) => wrap(curr + 1));

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); next(); }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [total]);

  const s = slides[i];
  return (
    <section
      ref={rootRef}
      className="case-carousel"
      role="region"
      aria-roledescription={t("carrusel", "carousel")}
      aria-label={resolvedAriaLabel}
      tabIndex={0}
      style={{ "--carousel-max-h": `${maxHeight}px` }}
    >
      <figure className="case-carousel-frame" aria-live="polite">
        <picture key={s.src}>
          <source srcSet={encodeURI(s.src)} type="image/webp" />
          <img
            src={encodeURI(s.fallback)}
            alt={aleL(s.alt, lang)}
            loading="lazy"
            decoding="async"
          />
        </picture>
      </figure>

      <div className="case-carousel-body">
        <div className="case-carousel-controls" role="group" aria-label={t("Navegación del carrusel", "Carousel navigation")}>
          <div className="case-carousel-dots" role="tablist" aria-label={t("Slides", "Slides")}>
            {slides.map((_, n) => (
              <button
                key={n}
                type="button"
                role="tab"
                aria-selected={n === i}
                aria-label={t(`Ir a la diapositiva ${n + 1} de ${total}`, `Go to slide ${n + 1} of ${total}`)}
                className={`case-carousel-dot ${n === i ? "is-on" : ""}`}
                onClick={() => go(n)}
              />
            ))}
            <span className="case-carousel-count mono" aria-hidden="true">
              {String(i + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
            </span>
          </div>
          <div className="case-carousel-nav">
            <button
              type="button"
              className="case-carousel-btn"
              onClick={prev}
              aria-label={t("Diapositiva anterior", "Previous slide")}
            >
              ←
            </button>
            <button
              type="button"
              className="case-carousel-btn"
              onClick={next}
              aria-label={t("Siguiente diapositiva", "Next slide")}
            >
              →
            </button>
          </div>
        </div>

        <p className="case-carousel-desc mono">
          <span className="case-carousel-kicker">{aleL(s.kicker, lang)}.</span> {aleL(s.body, lang)}
        </p>
      </div>
    </section>
  );
}

// ─── iPad mockup (portado del entregable de Claude Design) ────────────────
// Marco aluminio + bezel negro + pantalla en portrait (800×1059). Recibe
// fuentes de video y un caption. Respeta prefers-reduced-motion: si el
// usuario lo pidió, no reproduce el loop automáticamente.
function IPadMockup({ webm, mp4, alt, caption, ariaLabel }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (mql.matches) {
        v.pause();
        v.removeAttribute("autoplay");
      } else {
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    };
    apply();
    // Reintento al tener data — algunos browsers difieren autoplay hasta
    // que hay buffer suficiente.
    const onReady = () => { if (!mql.matches && v.paused) apply(); };
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("canplay", onReady);
    mql.addEventListener?.("change", apply);
    return () => {
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("canplay", onReady);
      mql.removeEventListener?.("change", apply);
    };
  }, []);
  return (
    <figure className="ipad-mockup" aria-label={ariaLabel}>
      <div className="ipad-mockup-frame">
        <span className="ipad-mockup-camera" aria-hidden="true" />
        <div className="ipad-mockup-bezel">
          <div className="ipad-mockup-screen">
            <video
              ref={videoRef}
              className="ipad-mockup-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-label={alt}
            >
              {webm && <source src={encodeURI(webm)} type="video/webm" />}
              {mp4 && <source src={encodeURI(mp4)} type="video/mp4" />}
            </video>
          </div>
        </div>
      </div>
      {caption && (
        <figcaption className="ipad-mockup-caption mono">{caption}</figcaption>
      )}
    </figure>
  );
}

// ─── MacBook mockup (portado del entregable de Claude Design) ─────────────
// Tapa con notch + base con bisagra/lip. Pantalla 16:10 con video adentro.
// Respeta prefers-reduced-motion (pausa el loop si el usuario lo pidió).
function MacMockup({ webm, mp4, alt, caption, ariaLabel, maxHeight = 500 }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const apply = () => {
      if (mql.matches) {
        v.pause();
        v.removeAttribute("autoplay");
      } else {
        const p = v.play();
        if (p && typeof p.catch === "function") p.catch(() => {});
      }
    };
    apply();
    const onReady = () => { if (!mql.matches && v.paused) apply(); };
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("canplay", onReady);
    mql.addEventListener?.("change", apply);
    return () => {
      v.removeEventListener("loadeddata", onReady);
      v.removeEventListener("canplay", onReady);
      mql.removeEventListener?.("change", apply);
    };
  }, []);
  return (
    <figure className="mac-mockup" aria-label={ariaLabel}>
      <div className="mac-mockup-stage" style={{ "--mac-max-h": `${maxHeight}px` }}>
        <div className="mac-mockup-lid">
          <span className="mac-mockup-notch" aria-hidden="true" />
          <div className="mac-mockup-display">
            <video
              ref={videoRef}
              className="mac-mockup-video"
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              aria-label={alt}
            >
              {webm && <source src={encodeURI(webm)} type="video/webm" />}
              {mp4 && <source src={encodeURI(mp4)} type="video/mp4" />}
            </video>
          </div>
        </div>
        <div className="mac-mockup-base">
          <span className="mac-mockup-lip" aria-hidden="true" />
        </div>
      </div>
      {caption && (
        <figcaption className="mac-mockup-caption mono">{caption}</figcaption>
      )}
    </figure>
  );
}

// ─── Case page ─────────────────────────────────────────────────────────────
function CaseALE({ onBack, lang = "es", setLang }) {
  const t = (es, en) => (lang === "en" ? en : es);
  return (
    <div className="case-page">
      <header className="process-nav">
        <div className="process-nav-inner shell">
          <CaseBreadcrumb onBackToProjects={onBack} lang={lang} />
          <span className="nav-brand">
            <span className="mark mono">anita<em>.</em>sevilla<em>.</em>ull</span>
          </span>
          <div className="process-nav-end">
            <span className="mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>{t("caso · agente ia", "case · ai agent")}</span>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </header>

      {/* 1 — HERO */}
      <section className="case-hero shell">
        <div className="case-hero-grid">
          <div className="case-hero-text">
            <div className="tag tag-line">{t("CASO AGENTE IA · AdTech", "AI AGENT CASE · AdTech")}</div>
            {lang === "en" ? (
              <h1 className="case-title mono">
                <span>
                  A <span className="case-hand">chatbot</span> that only watched.
                  Now it takes the lead.
                </span>
              </h1>
            ) : (
              <h1 className="case-title mono">
                <span>
                  Un <span className="case-hand">chatbot</span> que solo miraba.
                  Ahora, toma la iniciativa.
                </span>
              </h1>
            )}
            <p className="case-headline mono">
              <span className="case-stat">{t("10 días", "10 days")}</span>
              <span>{t(" para convertir un asistente pasivo en personaje agéntico.", " to turn a passive assistant into an agentic character.")}</span>
            </p>
            <p className="case-sub">
              {t(
                "Todo con IA — desde la investigación hasta el prototipo con vibe-design.",
                "All AI — from the research to the prototype with vibe-design."
              )}
            </p>

            <p className="case-meta mono">
              <b>{t("Proyecto confidencial", "Confidential project")}</b>
              <span> · 2026 · </span>
              <mark className="case-meta-mark">interactional designer</mark>
              <span> · AdTech</span>
            </p>
          </div>

          <figure className="case-hero-media case-hero-media--orb">
            <OrbAle className="orb-ale--hero" />
          </figure>
        </div>
      </section>

      {/* Nota de confidencialidad */}
      <Appear className="case-confidential shell" aria-label={t("Nota de confidencialidad", "Confidentiality note")}>
        <span className="tag tag-line">{t("Nota de confidencialidad", "Confidentiality note")}</span>
        {lang === "en" ? (
          <p className="case-confidential-body mono">
            This case is protected by an NDA. The client name and the product
            name have been omitted. <strong>ELIA</strong> is a fictional name
            created to present this work in portfolio. Quantitative data are
            approximations that preserve real scale without exposing internal
            metrics.
          </p>
        ) : (
          <p className="case-confidential-body mono">
            Este caso está protegido por un acuerdo de confidencialidad. Los
            nombres del cliente y del producto han sido omitidos.{" "}
            <strong>ELIA</strong> es un nombre ficticio creado para presentar
            este trabajo en portfolio. Los datos cuantitativos son aproximaciones
            que preservan la escala real sin exponer métricas internas.
          </p>
        )}
      </Appear>

      {/* 2 — CONTEXTO */}
      <Appear className="case-section shell">
        <div className="section-head">
          <span className="tag">{t("// CONTEXTO", "// CONTEXT")}</span>
        </div>
        <div className="case-context-grid">
          <div className="case-prose">
            {lang === "en" ? (
              <React.Fragment>
                <p>
                  The company generates ad campaigns on Meta, TikTok and Google
                  in a few clicks. With AI.
                </p>
                <p>
                  The product was AI. The build process was AI too.{" "}
                  <strong>The problem was the assistant inside.</strong>
                </p>
                <p>
                  ELIA existed. She was a chatbot that answered questions.{" "}
                  <strong>She didn't act. She didn't step in. She didn't guide.</strong>
                </p>
                <p>
                  Users dropped off at the hardest moments — connecting Meta,
                  configuring the first campaign, reading the results — and ELIA
                  watched.
                </p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p>
                  La empresa genera campañas publicitarias en Meta, TikTok y Google
                  en pocos clics. Con inteligencia artificial.
                </p>
                <p>
                  El producto era IA. El proceso de construcción también.{" "}
                  <strong>El problema era el asistente adentro.</strong>
                </p>
                <p>
                  ELIA existía. Era un chatbot que respondía preguntas.{" "}
                  <strong>No actuaba. No intervenía. No guiaba.</strong>
                </p>
                <p>
                  Los usuarios se caían del flujo en los momentos más difíciles —
                  conectar Meta, configurar la primera campaña, leer los resultados
                  — y ELIA miraba.
                </p>
              </React.Fragment>
            )}
          </div>
          <aside className="case-highlight" aria-label={t("La visión", "The vision")}>
            <span className="case-highlight-eyebrow tag">{t("La visión del producto", "The product vision")}</span>
            {lang === "en" ? (
              <p>
                Multiple AI agents working in parallel for each user.{" "}
                <strong>ELIA had to be the bridge.</strong> And for that, she
                needed an identity first.
              </p>
            ) : (
              <p>
                Múltiples agentes de IA trabajando en paralelo para cada usuario.{" "}
                <strong>ELIA tenía que ser el puente.</strong> Para eso, primero
                había que darle identidad.
              </p>
            )}
          </aside>
        </div>
      </Appear>

      {/* 4 — EL TOOLKIT */}
      <Appear className="case-section shell">
        <div className="section-head">
          <span className="tag">{t("// EL TOOLKIT", "// THE TOOLKIT")}</span>
        </div>
        <div className="case-toolkit">
          {ALE_TOOLKIT.map((tool) => (
            <span key={tool} className="chip">{tool}</span>
          ))}
        </div>
        <div className="case-prose">
          {lang === "en" ? (
            <p>
              The whole process was <span className="case-hand">AI-driven</span>{" "}
              — research artifacts, visual prototypes, even implementation with
              vibe-coding.
            </p>
          ) : (
            <p>
              Todo el proceso fue <span className="case-hand">AI-driven</span> —
              desde los artefactos de investigación, los prototipos visuales,
              hasta la implementación con vibe-coding.
            </p>
          )}
          {lang === "en" ? (
            <p>
              Team coordination ran on Discord, in real time.{" "}
              <strong>No office, but the feeling of being in one.</strong>
            </p>
          ) : (
            <p>
              La coordinación con el equipo fue por Discord, en tiempo real.{" "}
              <strong>Sin oficina, pero con la sensación de estar en una.</strong>
            </p>
          )}
        </div>
      </Appear>

      {/* 5 — EL PROCESO */}
      <section className="case-section shell" id="case-proceso">
        <div className="section-head">
          <span className="tag">{t("// EL PROCESO · 3 movimientos", "// THE PROCESS · 3 moves")}</span>
        </div>

        <Appear className="case-process-intro">
          <figure className="case-process-diagram">
            <picture>
              <source srcSet={encodeURI("assets/cases/ALE/proceso.webp")} type="image/webp" />
              <img
                src={encodeURI("assets/cases/ALE/proceso.png")}
                alt={t(
                  "Diagrama del proceso en tres movimientos: una lupa rodeada de notas (entender), una diana con bandera clavada en el centro (definir), y un cubo wireframe (materializar), unidos por flechas punteadas.",
                  "Diagram of the three-move process: a magnifier surrounded by sticky notes (Understand), a target with a flag in the bullseye (Define), and a wireframe cube (Materialize), connected by dashed arrows."
                )}
                loading="lazy"
                decoding="async"
              />
            </picture>
            <figcaption className="case-process-labels" aria-hidden="true">
              <span className="case-hand">{t("Entender", "Understand")}</span>
              <span className="case-hand">{t("Definir", "Define")}</span>
              <span className="case-hand">{t("Materializar", "Materialize")}</span>
            </figcaption>
          </figure>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("01 · Entender", "01 · Understand")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "Empecé leyendo el sistema. Audité la plataforma, identifiqué los puntos de fricción y abandono, y mapeé en qué momentos del flujo ELIA podría haber actuado — y no lo hizo.",
                "I started by reading the system. I audited the platform, identified the friction and drop-off points, and mapped the moments in the flow where ELIA could have acted — and didn't."
              )}
            </p>
          </div>

          <CaseCarousel
            slides={ALE_DATOS}
            maxHeight={600}
            ariaLabel={t("Hallazgos de datos que informaron el rediseño de ELIA", "Data findings that informed ELIA's redesign")}
            lang={lang}
          />

          <div className="case-prose">
            {lang === "en" ? (
              <React.Fragment>
                <p>
                  I crossed the conversation data with what I knew about
                  behavioral psychology and conversational design.{" "}
                  <strong>The problem wasn't technical — it was emotional.</strong>{" "}
                  The flow was asking the user to trust a system that hadn't given
                  them a reason to yet. Most of them weren't leaving because they
                  didn't understand the steps. They were leaving because what
                  they felt most was frustration.
                </p>
                <p>With that clear, it was time to document.</p>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <p>
                  Crucé los datos de conversaciones con lo que sabía de psicología
                  del comportamiento y diseño conversacional.{" "}
                  <strong>El problema no era técnico: era emocional.</strong>{" "}
                  El flujo le pedía al usuario que confiara en un sistema que
                  todavía no le había dado ninguna razón para hacerlo. La gran
                  mayoría no se iba porque no entendía los pasos. Se iba porque
                  lo que más sentían era frustración.
                </p>
                <p>Con eso claro, era hora de documentar.</p>
              </React.Fragment>
            )}
          </div>

          <div className="case-journey-row">
            <figure className="case-journey-fig">
              <picture>
                <source
                  srcSet={encodeURI("assets/cases/ALE/journey_map.webp")}
                  type="image/webp"
                />
                <img
                  src={encodeURI("assets/cases/ALE/journey_map.png")}
                  alt={t(
                    "Journey map de onboarding de ELIA renderizado en un iPad: encabezado 'Journey Map — Onboarding ELIA', curva de etapas con picos y valles que marcan momentos críticos, y una grilla por debajo que cruza cada etapa con sentimiento del usuario, acciones, oportunidades de intervención y métricas.",
                    "ELIA onboarding journey map rendered on an iPad: header 'Journey Map — ELIA Onboarding', a curve of stages with peaks and valleys marking critical moments, and a grid below crossing each stage with user sentiment, actions, intervention opportunities and metrics."
                  )}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <figcaption className="case-journey-caption mono">
                {t(
                  "Journey map · onboarding ELIA — construido en conversación con Claude",
                  "Journey map · ELIA onboarding — built in conversation with Claude"
                )}
              </figcaption>
            </figure>

            <figure className="case-journey-fig case-journey-fig--flat">
              <picture>
                <source
                  srcSet={encodeURI("assets/cases/ALE/presentacion_stakeholder.webp")}
                  type="image/webp"
                />
                <img
                  src={encodeURI("assets/cases/ALE/presentacion_stakeholder.png")}
                  alt={t(
                    "Slide de la presentación a stakeholders titulada 'Flujo Propuesto — Emocional, Preventivo y Adictivo': curva emocional del usuario por etapa del onboarding, tarjetas con datos del problema y la oportunidad (+80%, +40%, +52%, 71%, 60–70%), y tres pilares de diseño — Personalización Temprana, Emotional Design y Voice vs Text — con métricas y descripciones de cada uno.",
                    "Stakeholder presentation slide titled 'Proposed Flow — Emotional, Preventive and Addictive': user emotional curve by onboarding stage, cards with problem and opportunity data (+80%, +40%, +52%, 71%, 60–70%), and three design pillars — Early Personalization, Emotional Design and Voice vs Text — with metrics and descriptions for each."
                  )}
                  loading="lazy"
                  decoding="async"
                />
              </picture>
              <figcaption className="case-journey-caption mono">
                {t(
                  "Presentación a stakeholders · flujo propuesto y pilares de diseño",
                  "Stakeholder presentation · proposed flow and design pillars"
                )}
              </figcaption>
            </figure>
          </div>

          <div className="case-prose case-journey-prose">
            {lang === "en" ? (
              <p>
                I built the journey maps, the service blueprint and the
                stakeholder presentations in conversation with Claude. No Figma,
                no hours pushing boxes around.{" "}
                <strong>Information in, artifact out.</strong>
              </p>
            ) : (
              <p>
                Los journey maps, el service blueprint y las presentaciones a
                stakeholders los construí en conversación con Claude. Sin Figma,
                ni horas moviendo cuadraditos.{" "}
                <strong>Información adentro, artefacto afuera.</strong>
              </p>
            )}
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("02 · Definir", "02 · Define")}</h2>
          <div className="case-definir-grid">
            <div className="case-prose case-definir-prose">
              <p>
                {t("Antes de una sola pantalla: ", "Before a single screen: ")}<strong>{t("quién es ELIA", "who ELIA is")}</strong>.
              </p>
              {lang === "en" ? (
                <p>
                  Her philosophy. Her personality. Her voice and tone. How she
                  handles errors. How she reacts to frustration. When she breaks
                  in and when she stays out.
                </p>
              ) : (
                <p>
                  Su filosofía. Su personalidad. Su voz y tono. Cómo maneja los
                  errores. Cómo reacciona ante la frustración. En qué momentos
                  irrumpe y en cuáles se mantiene al margen.
                </p>
              )}
              <p>
                {t(
                  "Todo documentado en un manual completo, publicado como documentación viva en Mintlify. Para los devs, para los diseñadores, para cualquiera que tocara a ELIA después.",
                  "All documented in a complete manual, published as living documentation in Mintlify. For devs, for designers, for anyone who touched ELIA afterward."
                )}
              </p>

              <aside className="case-toc-box" aria-label={t("Secciones del manual de ELIA en Mintlify", "Sections of ELIA's manual in Mintlify")}>
                <span className="case-toc-box-eyebrow tag">{t("Manual de ELIA · 8 secciones", "ELIA's manual · 8 sections")}</span>
                <ul className="case-toc-list mono">
                  <li>{t("Introducción y Filosofía", "Introduction & Philosophy")}</li>
                  <li>{t("Personalidad, Voz y Tono", "Personality, Voice & Tone")}</li>
                  <li>{t("Estados Visuales y Comportamiento", "Visual States & Behavior")}</li>
                  <li>{t("Acciones Recurrentes y Comportamientos", "Recurring Actions & Behaviors")}</li>
                  <li>{t("Transiciones y Animaciones", "Transitions & Animations")}</li>
                  <li>{t("Flujos Específicos", "Specific Flows")}</li>
                  <li>{t("Manejo de Errores", "Error Handling")}</li>
                  <li>{t("Reglas Técnicas y Accesibilidad", "Technical Rules & Accessibility")}</li>
                </ul>
              </aside>
            </div>

            <IPadMockup
              webm="assets/cases/ALE/manual-ale.webm"
              mp4="assets/cases/ALE/manual-ale.mp4"
              alt={t(
                "Recorrido animado del manual de ELIA en Mintlify: portada, voz y tono, manejo de errores y estados visuales.",
                "Animated walkthrough of ELIA's manual in Mintlify: cover, voice and tone, error handling and visual states."
              )}
              ariaLabel={t("Manual oficial de ELIA · Mint Starter Kit visto en un iPad", "ELIA's official manual · Mint Starter Kit on an iPad")}
              caption={t("Manual de ELIA · documentación viva en Mintlify", "ELIA's manual · living documentation in Mintlify")}
            />
          </div>
        </Appear>

        <Appear className="case-sub-section">
          <h2 className="case-h3 mono">{t("03 · Materializar", "03 · Materialize")}</h2>
          <div className="case-prose">
            <p>
              {t(
                "Cada estado tiene variantes emocionales. Cada transición tiene pacing y lógica. Prototipé en Lovable y Figma Make, rápido e iterativo, sin sobre-producción.",
                "Each state has emotional variants. Each transition has pacing and logic. I prototyped in Lovable and Figma Make — fast and iterative, no over-production."
              )}
            </p>
            <p>{t("ELIA tiene cuatro estados visuales:", "ELIA has four visual states:")}</p>
          </div>
          <ul className="case-states">
            {ALE_STATES.map((s) => (
              <li key={aleL(s.tag, "es")} className="case-state">
                <figure className="case-state-orb">
                  <picture>
                    <source srcSet={encodeURI(s.img)} type="image/webp" />
                    <img
                      src={encodeURI(s.fallback)}
                      alt={aleL(s.alt, lang)}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                </figure>
                <div className="case-state-head">
                  <span className="case-state-tag mono">{aleL(s.tag, lang)}</span>
                  <span className="case-state-role mono">{aleL(s.role, lang)}</span>
                </div>
                <span className="case-state-body">{aleL(s.body, lang)}</span>
              </li>
            ))}
          </ul>
          <div className="case-prose">
            {lang === "en" ? (
              <p>
                But we weren't just trying to make pretty orbs and pretty
                screens. I was designing a relationship. By applying the{" "}
                <span className="case-hand">Endowment Effect</span> and
                structured emotional design, we got users to feel ELIA was
                theirs from minute one.
              </p>
            ) : (
              <p>
                Pero no buscábamos solo hacer orbes y pantallas bonitas. Buscaba
                diseñar una relación. Aplicando el{" "}
                <span className="case-hand">Endowment Effect</span> y el diseño
                emocional estructurado, logramos que el usuario sienta que ELIA
                es suya desde el primer minuto.
              </p>
            )}
          </div>
          <MacMockup
            webm="assets/cases/ALE/ale-demo.webm"
            mp4="assets/cases/ALE/ale-demo.mp4"
            alt={t(
              "Recorrido en loop del producto terminado de ELIA corriendo en la plataforma: el asistente interviniendo proactivamente en distintos momentos del flujo de configuración de campañas, mostrando sus estados sólido, líquido, gaseoso y offline.",
              "Looping walkthrough of the finished ELIA product running on the platform: the assistant intervening proactively at different moments in the campaign-setup flow, showing its solid, liquid, gaseous and offline states."
            )}
            ariaLabel={t("Demo del producto terminado de ELIA en un MacBook", "Demo of the finished ELIA product on a MacBook")}
            caption={t("ELIA en acción · demo del producto terminado", "ELIA in action · finished-product demo")}
            maxHeight={500}
          />
        </Appear>
      </section>

      {/* 6 — REVIEWS */}
      <Appear className="case-section shell">
        <div className="section-head">
          <span className="tag">{t("// LO QUE ME DIJERON", "// WHAT THEY TOLD ME")}</span>
        </div>
        <div className="case-reviews">
          {ALE_REVIEWS.map((r, i) => (
            <figure className="case-review" key={i}>
              <blockquote className="case-review-text">"{aleL(r.text, lang)}"</blockquote>
              <figcaption className="case-review-who">
                <span className="case-review-avatar mono" aria-hidden="true">
                  {r.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                </span>
                <span className="case-review-meta">
                  <strong>{r.name}</strong>
                  <span className="case-review-role mono">{aleL(r.role, lang)}</span>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Appear>

      {/* 7 — CIERRE */}
      <Appear className="case-section case-section--quote shell">
        {lang === "en" ? (
          <blockquote className="case-quote mono">
            <span className="case-quote-line">"What would have taken three months took two weeks.</span>
            <span className="case-quote-line">It's not just speed,</span>
            <span className="case-quote-line">it's a <span className="accent">new way</span></span>
            <span className="case-quote-line">of working."</span>
          </blockquote>
        ) : (
          <blockquote className="case-quote mono">
            <span className="case-quote-line">"Lo que hubiera tardado tres meses tardó dos semanas.</span>
            <span className="case-quote-line">No es solo velocidad,</span>
            <span className="case-quote-line">es una <span className="accent">nueva forma</span></span>
            <span className="case-quote-line">de trabajar."</span>
          </blockquote>
        )}
        <div className="case-cierre-orb" aria-hidden="true">
          <OrbAle ariaLabel={t("ELIA — orbe agéntico", "ELIA — agentic orb")} />
        </div>
        <p className="case-thanks mono" aria-label={t("Gracias por leer", "Thanks for reading")}>
          <span aria-hidden="true">❤︎</span> {t("Gracias por leer.", "Thanks for reading.")}
        </p>
      </Appear>

      <FooterCaseCondensed
        otherCaseLabel={t("Ver caso DJY Pro", "See DJY Pro case")}
        otherCaseHref="#caso-djy"
        lang={lang}
      />
    </div>
  );
}

window.CaseALE = CaseALE;
